import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { localDb, LocalProfile, initializeLocalDatabase } from '@/lib/localDatabase';
import { getCurrencyByCode, type Currency } from '@/lib/currencies';

export type WeekStart = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
export type DefaultPeriod = 'week' | 'month' | 'quarter' | 'year';

export const weekStartOptions: { value: WeekStart; label: string }[] = [
  { value: 'monday', label: 'Понедельник' },
  { value: 'tuesday', label: 'Вторник' },
  { value: 'wednesday', label: 'Среда' },
  { value: 'thursday', label: 'Четверг' },
  { value: 'friday', label: 'Пятница' },
  { value: 'saturday', label: 'Суббота' },
  { value: 'sunday', label: 'Воскресенье' },
];

export const periodOptions: { value: DefaultPeriod; label: string }[] = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'year', label: 'Год' },
];

export function useLocalUserProfile() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<LocalProfile | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Initialize database and fetch profile
  useEffect(() => {
    const init = async () => {
      try {
        await initializeLocalDatabase();
        const prof = await localDb.profile.get();
        setProfile(prof || null);
        setLoading(false);
      } catch (error) {
        console.error('Error initializing local profile:', error);
        setLoading(false);
      }
    };

    init();
  }, [refreshKey]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // Get current currency
  const currency: Currency = getCurrencyByCode(profile?.currency || 'KZT');

  // Helper to get label for week start
  const getWeekStartLabel = useCallback(() => {
    const option = weekStartOptions.find(o => o.value === profile?.week_start);
    return option?.label || 'Понедельник';
  }, [profile]);

  // Helper to get label for default period
  const getPeriodLabel = useCallback(() => {
    const option = periodOptions.find(o => o.value === profile?.default_period);
    return option?.label || 'Месяц';
  }, [profile]);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<LocalProfile>) => {
    try {
      await localDb.profile.update(updates);

      toast({
        title: 'Сохранено',
        description: 'Настройки успешно обновлены',
      });

      refresh();
      return true;
    } catch (error) {
      console.error('Error updating local profile:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось сохранить настройки',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast, refresh]);

  return {
    profile: profile ? {
      ...profile,
      user_id: 'local',
    } : null,
    loading,
    user: { id: 'local' },
    currency,
    updateProfile,
    refetch: refresh,
    getWeekStartLabel,
    getPeriodLabel,
  };
}
